import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from '../util/amplifyAuth';
import { saveFocuses, getUserProfile } from '../components/API';
import { CATEGORY_DEFINITIONS } from '../data/categoryDefinitions';
import { tierFromGroups } from '../util/featureConfig';
import { getCachedProfile, setCachedProfile } from '../util/CacheUtil';
import Spinner from '../components/Spinner/Spinner';
import '../styles/Signup.css';
import '../styles/SelectFocus.css';

const CATEGORY_ORDER = ['performance', 'recovery', 'longevity', 'wellness'];

const CATEGORY_ICONS = {
    performance: (
        <svg viewBox="0 0 64 64" className="sf-engraved-icon">
            <path d="M32 8l6 14h15l-12 9 5 15-14-10-14 10 5-15-12-9h15z" />
        </svg>
    ),
    recovery: (
        <svg viewBox="0 0 64 64" className="sf-engraved-icon">
            <path d="M32 52c-12-8-20-16-20-24a12 12 0 0 1 20-9 12 12 0 0 1 20 9c0 8-8 16-20 24z" />
        </svg>
    ),
    longevity: (
        <svg viewBox="0 0 64 64" className="sf-engraved-icon">
            <circle cx="32" cy="32" r="20" fill="none" strokeWidth="3" />
            <path d="M32 16v16l10 10" fill="none" strokeWidth="3" strokeLinecap="round" />
        </svg>
    ),
    wellness: (
        <svg viewBox="0 0 64 64" className="sf-engraved-icon">
            <path d="M32 10c-4 0-8 6-8 14 0 10 8 30 8 30s8-20 8-30c0-8-4-14-8-14z" />
            <path d="M22 28c-3-3-10-4-14 0s-2 12 6 18c4 3 12 6 18 8" fill="none" strokeWidth="2" />
            <path d="M42 28c3-3 10-4 14 0s2 12-6 18c-4 3-12 6-18 8" fill="none" strokeWidth="2" />
        </svg>
    ),
};

export default function SelectFocus() {
    const [selectedFocuses, setSelectedFocuses] = useState(() => {
        const cached = getCachedProfile();
        return new Set(cached?.focuses || []);
    });
    const [flippedCategories, setFlippedCategories] = useState(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [tier, setTier] = useState(0);
    const navigate = useNavigate();

    const isPremium = tier >= 1;

    useEffect(() => {
        (async () => {
            try {
                await getCurrentUser();
                const session = await fetchAuthSession();
                const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];
                setTier(tierFromGroups(groups));
                const profile = await getUserProfile();
                if (profile?.focuses?.length) {
                    setSelectedFocuses(new Set(profile.focuses));
                }
            }
            catch { navigate('/login', { replace: true }); }
        })();
    }, [navigate]);

    const lockedCategory = (() => {
        if (isPremium) return null;
        for (const fId of selectedFocuses) {
            for (const catId of CATEGORY_ORDER) {
                if (fId in CATEGORY_DEFINITIONS[catId].focuses) return catId;
            }
        }
        return null;
    })();

    const toggleFocus = (focusId) => {
        setSelectedFocuses(prev => {
            const next = new Set(prev);
            if (next.has(focusId)) {
                next.delete(focusId);
            } else if (isPremium || next.size < 3) {
                next.add(focusId);
            }
            return next;
        });
    };

    const handleCardClick = useCallback((catId) => {
        setFlippedCategories(prev => {
            const next = new Set(prev);
            if (next.has(catId)) next.delete(catId);
            else next.add(catId);
            return next;
        });
    }, [lockedCategory]);

    const handleSubmit = async () => {
        if (selectedFocuses.size === 0) return;
        setSubmitting(true);
        setError('');
        try {
            const arr = [...selectedFocuses];
            await saveFocuses(arr);
            const cached = getCachedProfile();
            if (cached) setCachedProfile({ ...cached, focuses: arr });
            navigate('/home');
        } catch {
            setError('Something went wrong. Please try again.');
            setSubmitting(false);
        }
    };

    const handleSkip = () => {
        localStorage.setItem('bloodeye_focus_skipped_until', String(Date.now() + 86400000));
        navigate('/home');
    };

    const hasFocusesInCategory = (catId) => {
        const cat = CATEGORY_DEFINITIONS[catId];
        return [...selectedFocuses].some(fId => fId in cat.focuses);
    };

    return (
        <div className="signup-page gate-scrollable">
            <div className="signup-overlay">
                <div className="signup-modal sf-modal">
                    <h1>What are your health priorities?</h1>
                    <p className="signup-subtitle">
                        {isPremium
                            ? 'Choose focus areas from any category to personalize your dashboard.'
                            : 'Pick a category, then choose up to 3 focus areas within it.'}
                    </p>

                    {error && <div className="signup-error">{error}</div>}

                    <div className="sf-card-grid">
                        {CATEGORY_ORDER.map(catId => {
                            const cat = CATEGORY_DEFINITIONS[catId];
                            const isFlipped = flippedCategories.has(catId);
                            const hasSelections = hasFocusesInCategory(catId);
                            const isLocked = lockedCategory && lockedCategory !== catId;
                            return (
                                <div
                                    key={catId}
                                    className={`sf-card-wrapper ${hasSelections ? 'sf-card-active' : ''}`}
                                    style={hasSelections ? { '--active-color': cat.color } : undefined}
                                >
                                    <div
                                        className={`sf-card-inner ${isFlipped ? 'sf-flipped' : ''}`}
                                    >
                                        <div
                                            className={`sf-card-face sf-card-front sf-metallic-${catId}`}
                                            onClick={() => handleCardClick(catId)}
                                        >
                                            <div className="sf-shine" />
                                            {CATEGORY_ICONS[catId]}
                                            <span className="sf-card-name">{cat.display_name}</span>
                                        </div>
                                        <div
                                            className={`sf-card-face sf-card-back sf-metallic-${catId}`}
                                            onClick={() => handleCardClick(catId)}
                                        >
                                            <span className="sf-back-title">{cat.display_name}</span>
                                            <div className="sf-focus-list">
                                                {Object.entries(cat.focuses).map(([fId, focus]) => {
                                                    const checked = selectedFocuses.has(fId);
                                                    const disabled = !checked && !isPremium && (selectedFocuses.size >= 3 || isLocked);
                                                    return (
                                                        <label
                                                            key={fId}
                                                            className={`sf-focus-row ${checked ? 'sf-focus-selected' : ''} ${disabled ? 'sf-focus-disabled' : ''}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                disabled={disabled}
                                                                onChange={() => toggleFocus(fId)}
                                                            />
                                                            <div className="sf-focus-info">
                                                                <span className="sf-focus-name">{focus.display_name}</span>
                                                                <span className="sf-focus-desc">{focus.description}</span>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <div className="sf-selection-count">
                                                {isPremium ? `${selectedFocuses.size} selected` : `${selectedFocuses.size}/3 selected`}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <button
                        className="signup-submit"
                        onClick={handleSubmit}
                        disabled={selectedFocuses.size === 0 || submitting}
                    >
                        {submitting ? <Spinner size={20} color="#fff" /> : 'Continue'}
                    </button>

                    <button className="sf-skip" onClick={handleSkip}>
                        Skip for now
                    </button>
                </div>
            </div>
        </div>
    );
}
